import { memo } from 'react';

const shimmer = 'motion-safe:animate-pulse bg-gray-200 dark:bg-gray-700 rounded';

// Common a11y bundle: skeletons must announce themselves to assistive tech.
const a11yProps = {
  role: 'status',
  'aria-live': 'polite',
  'aria-busy': 'true',
};

function SrLoadingLabel() {
  return <span className="sr-only">Loading…</span>;
}

function ListSkeleton() {
  return (
    <div data-skeleton="list" className="p-6 space-y-4" {...a11yProps}>
      <SrLoadingLabel />
      <div className={`${shimmer} h-10 w-64`} />
      <div className="space-y-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className={`${shimmer} h-14 w-full`} />
        ))}
      </div>
    </div>
  );
}

function FormSkeleton() {
  return (
    <div data-skeleton="form" className="p-6 space-y-6 max-w-3xl" {...a11yProps}>
      <SrLoadingLabel />
      <div className={`${shimmer} h-8 w-48`} />
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <div className={`${shimmer} h-4 w-32`} />
          <div className={`${shimmer} h-10 w-full`} />
        </div>
      ))}
      <div className={`${shimmer} h-10 w-32`} />
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div data-skeleton="detail" className="p-6 space-y-4" {...a11yProps}>
      <SrLoadingLabel />
      <div className={`${shimmer} h-8 w-72`} />
      <div className={`${shimmer} h-4 w-1/2`} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className={`${shimmer} h-24 w-full`} />
        ))}
      </div>
    </div>
  );
}

function GenericSkeleton() {
  return (
    <div
      data-skeleton="generic"
      className="flex min-h-[50vh] items-center justify-center px-4"
      {...a11yProps}
    >
      <div className="flex flex-col items-center gap-4">
        <div
          className="h-12 w-12 border-4 border-primary-500 border-t-transparent motion-safe:animate-spin"
          aria-hidden
        />
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      </div>
    </div>
  );
}

function RouteSkeleton({ variant = 'generic' }) {
  switch (variant) {
    case 'list':   return <ListSkeleton />;
    case 'form':   return <FormSkeleton />;
    case 'detail': return <DetailSkeleton />;
    default:       return <GenericSkeleton />;
  }
}

export default memo(RouteSkeleton);
